/**
 * iter_193 — 🎧 RGM Radio Card (Dashboard listening entry point).
 *
 * Three modes:
 *   • Songs (DEFAULT)   → /api/shuffle/radio?station=artist  (tracks only)
 *   • Beat Radio        → /api/shuffle/radio?station=producer (beats only)
 *   • Everything        → /api/shuffle/radio?station=community (interleaved)
 *
 * Plus quick-launch genre chips. Reuses the existing global `usePlayer()`
 * queue engine — DOES NOT create a second audio element. Tracks/beats
 * carry the `_station` marker so the ShuffleRadioActions bar surfaces
 * radio-style controls (Like / Repost / Gift / Comment / Open).
 */
import { Component, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";

// iter_193d — Local, section-scoped ErrorBoundary. Guarantees the RGM
// Radio slot ALWAYS renders something visible (a compact fallback with
// heading + Retry) even if the child throws — so a stale JS chunk,
// missing runtime dependency, or hook exception can never silently hide
// the section on the Dashboard. Never falls back to a full-screen error.
class RadioSectionBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) {
    console.error("[RgmRadio] section render error:", err);
  }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <Card
        data-testid="rgm-radio-card"
        className="bg-gradient-to-br from-purple-900/40 via-black/60 to-pink-900/30 border-purple-500/30 p-6"
      >
        <h3
          className="text-xl font-bold text-white flex items-center gap-2"
          data-testid="rgm-radio-title"
        >
          🎧 <span>RGM Radio</span>
        </h3>
        <p className="text-sm text-purple-200/80 mt-1" data-testid="rgm-radio-hint">
          The radio hit a snag — reload the page to bring it back.
        </p>
        <Button
          onClick={() => { if (typeof window !== "undefined") window.location.reload(); }}
          data-testid="rgm-radio-launch-btn"
          className="mt-4 w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold px-6 py-2.5"
        >
          ↻  Reload radio
        </Button>
      </Card>
    );
  }
}

const MODES = [
  { id: "songs", label: "Songs", station: "artist", icon: "🎤",
    hint: "Original tracks from artists — no beats." },
  { id: "beat_radio", label: "Beat Radio", station: "producer", icon: "🎧",
    hint: "Instrumentals & type beats from producers." },
  { id: "everything", label: "Everything", station: "community", icon: "🔀",
    hint: "Tracks and beats interleaved, powered by RGM Shuffle." },
];

const CATEGORIES = [
  { id: "all", label: "All Genres", value: null },
  { id: "hip-hop", label: "Hip-Hop", value: "hip-hop" },
  { id: "rnb", label: "R&B", value: "r&b" },
  { id: "pop", label: "Pop", value: "pop" },
  { id: "latin", label: "Latin", value: "latin" },
  { id: "afrobeats", label: "Afrobeats", value: "afrobeats" },
];

const RgmRadioCard = () => {
  const { play } = usePlayer();
  const [mode, setMode] = useState("songs");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(false);
  // iter_193d — inline error state so a fetch failure still shows the
  // card with a visible retry affordance instead of silently reverting.
  const [error, setError] = useState(null);

  const active = MODES.find((m) => m.id === mode) || MODES[0];
  const activeCategory = CATEGORIES.find((c) => c.id === category) || CATEGORIES[0];

  const launch = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ station: active.station, limit: "50" });
      if (activeCategory.value) params.set("genre", activeCategory.value);
      const res = await api.get(`/shuffle/radio?${params.toString()}`);
      const queue = Array.isArray(res.data?.queue) ? res.data.queue : [];
      if (queue.length === 0) {
        setError("empty");
        toast.info("Nothing to play right now — try a different mode or category.");
        return;
      }
      // Stamp _station so ShuffleRadioActions renders + play-count event dispatches.
      const stamped = queue.map((it) => ({
        ...it,
        _station: active.station,
        sourceLabel: `RGM Radio · ${active.label}${activeCategory.value ? ` · ${activeCategory.label}` : ""}`,
      }));
      play(stamped[0], {
        queue: stamped,
        sourceLabel: `RGM Radio · ${active.label}${activeCategory.value ? ` · ${activeCategory.label}` : ""}`,
      });
      toast.success(`🎧 ${active.label} radio started · ${queue.length} in queue`);
    } catch (e) {
      setError("fetch_failed");
      toast.error("Could not start RGM Radio. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      data-testid="rgm-radio-card"
      className="bg-gradient-to-br from-purple-900/40 via-black/60 to-pink-900/30 border-purple-500/30 p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2" data-testid="rgm-radio-title">
            🎧 <span>RGM Radio</span>
          </h3>
          <p className="text-sm text-purple-200/80 mt-1" data-testid="rgm-radio-hint">
            {active.hint}
          </p>
        </div>
      </div>

      {/* Mode selector */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="rgm-radio-modes">
        {MODES.map((m) => {
          const isActive = m.id === mode;
          return (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              data-testid={`rgm-radio-mode-${m.id}`}
              aria-pressed={isActive}
              className={`px-3.5 py-2 rounded-full text-sm font-semibold border transition-colors ${
                isActive
                  ? "bg-purple-500 border-purple-300 text-white shadow-lg shadow-purple-500/30"
                  : "bg-white/5 border-white/10 text-gray-200 hover:bg-purple-500/20 hover:border-purple-400/50"
              }`}
            >
              <span className="mr-1.5">{m.icon}</span>{m.label}
            </button>
          );
        })}
      </div>

      {/* Quick-launch category chips */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="rgm-radio-categories">
        {CATEGORIES.map((c) => {
          const isActive = c.id === category;
          return (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              data-testid={`rgm-radio-category-${c.id}`}
              aria-pressed={isActive}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                isActive
                  ? "bg-pink-500/30 border-pink-300 text-pink-50"
                  : "bg-white/5 border-white/10 text-gray-300 hover:bg-pink-500/15 hover:border-pink-400/40"
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Launch button */}
      <Button
        onClick={launch}
        disabled={loading}
        data-testid="rgm-radio-launch-btn"
        className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold px-6 py-2.5"
      >
        {loading
          ? "Tuning…"
          : error === "fetch_failed"
          ? `↻  Retry ${active.label}`
          : `▶  Start ${active.label}${activeCategory.value ? ` · ${activeCategory.label}` : ""}`}
      </Button>

      {/* Inline error state — visible even before playback data loads. */}
      {error === "fetch_failed" && (
        <p
          className="text-[12px] text-rose-300 mt-2"
          data-testid="rgm-radio-error"
          role="status"
        >
          Could not reach the radio just now. Tap Retry.
        </p>
      )}
      {error === "empty" && (
        <p
          className="text-[12px] text-amber-200 mt-2"
          data-testid="rgm-radio-empty"
          role="status"
        >
          Nothing playing in this station right now — try another mode or category.
        </p>
      )}

      <p className="text-[11px] text-gray-500 mt-3">
        Continuous stream · uses the global player so it keeps going while you browse.
      </p>
    </Card>
  );
};

export { RgmRadioCard };

/**
 * SafeRgmRadioCard (default export) — enforces the section-scoped
 * ErrorBoundary. The Dashboard imports the default. If the underlying
 * card ever throws (bad chunk, missing dep, hook error) the boundary
 * still renders a visible RGM Radio slot with a Retry/Reload button,
 * so the section NEVER silently disappears on mobile or desktop.
 */
const SafeRgmRadioCard = () => (
  <RadioSectionBoundary>
    <RgmRadioCard />
  </RadioSectionBoundary>
);

export default SafeRgmRadioCard;
